import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import { all_routes } from "../../router/all_routes";
import DealsDeailsModal from "../../../core/modals/userReportModal";
import DefaultEditor from "react-simple-wysiwyg";
import { useDispatch, useSelector } from "react-redux";
import { setActivityTogglePopup } from "../../../core/data/redux/commonSlice";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header";
import {
  accountType,
  ascendingandDecending,
  documentType,
  LocaleData,
  status,
  statusList,
} from "../../../core/common/selectoption/selectoption";
import { SelectWithImage2 } from "../../../core/common/selectWithImage2";
import { Deal, fetchAllDeals } from "../../../api/deal";
import {
  DealHistoryItem,
  DealHistoryResponse,
  DealStatusChange,
  fetchDealHistory,
  extractStatusChanges,
} from "../../../api/dealtimeline";
import { Button } from "react-bootstrap";
import { isToday, isYesterday, isThisWeek, isThisMonth } from "date-fns";
import { FaPen, FaPlus } from "react-icons/fa";
import { Alert, Spin, Timeline } from "antd";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
interface TimelineItem {
  date: string;
  time: string;
  modified_by: string;
  changes: string;
  type: string;
}

type GroupedHistory = {
  [groupLabel: string]: TimelineItem[];
};

const DealsDetails = () => {
  const statusClassMap: Record<string, string> = {
    Won: "bg-success",
    Lost: "bg-danger",
    Closed: "bg-warning",
    Open: "bg-primary",
    default: "bg-secondary",
  };

  const statusIconMap: Record<string, JSX.Element> = {
    Won: <i className="ti ti-check me-1" />,
    Closed: <i className="ti ti-handshake me-1" />,
    Open: <i className="ti ti-progress me-1" />,
    Lost: <i className="ti ti-phone-off me-1" />,
  };

  const route = all_routes;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [statusChanges, setStatusChanges] = useState<DealStatusChange[]>([]);
  const location = useLocation();
  useEffect(() => {
    const loadDeals = async () => {
      const result = await fetchAllDeals();
      setDeals(result);
    };
    loadDeals();
  }, []);

  const [timelineData, setTimelineData] = useState<GroupedHistory>({});
  const [openDates, setOpenDates] = useState<string[]>([]);
  const dealFromState = location.state as Deal | null;
  const { deal_id: paramDealId } = useParams<{ deal_id: string }>();

  const dealId = paramDealId || dealFromState?.deal_id;
  const deal = deals.find((d) => d.deal_id === dealId) || dealFromState;

  useEffect(() => {
    const loadTimeline = async () => {
      if (!dealId) return;
      try {
        const data = await fetchDealHistory(dealId);
        setTimelineData(prepareDealTimeline(data));
        const changes = extractStatusChanges(data);
        setStatusChanges(changes);
      } catch (err) {
        console.error("Failed to load deal timeline:", err);
        setError("Failed to load deal history");
      } finally {
        setLoading(false);
      }
    };
    loadTimeline();
  }, [dealId]);

  if (!deal) return <p>Deal not found.</p>;

  const toggleDate = (group: string) => {
    setOpenDates((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    );
  };

  const getDateGroupLabel = (date: string): string => {
    const d = new Date(date);
    if (isToday(d)) return "Today";
    if (isYesterday(d)) return "Yesterday";
    if (isThisWeek(d, { weekStartsOn: 1 })) return "This Week";
    if (isThisMonth(d)) return "This Month";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const prepareDealTimeline = (data: DealHistoryResponse): GroupedHistory => {
    const formatItem = (item: DealHistoryItem, type: string): TimelineItem => {
      const localDate = new Date(item.modified_at);
      const date = localDate.toISOString().split("T")[0];
      const time = localDate.toTimeString().split(" ")[0];

      const changes = Object.entries(item.changed_values)
        .map(([field, value]) => {
          const from = value.old || "-";
          const to = value.new || "-";
          return `<b>${field}</b> was updated from <b>${from}</b> to <b>${to}</b>`;
        })
        .join("<br/>");

      return {
        date,
        time,
        modified_by: item.modified_by,
        changes,
        type,
      };
    };

    const createdAt = new Date(data.deal_created_at);
    const createdEntry: TimelineItem = {
      date: createdAt.toISOString().split("T")[0],
      time: createdAt.toTimeString().split(" ")[0],
      modified_by: "System",
      changes: "<b>Deal</b> was <b>created</b>.",
      type: "created",
    };

    const allItems = [
      ...data.deal_history.map((h) => formatItem(h, "deal")),
      createdEntry,
    ].sort((a, b) => {
      const aDate = new Date(`${a.date}T${a.time}`);
      const bDate = new Date(`${b.date}T${b.time}`);
      return bDate.getTime() - aDate.getTime(); // newest first
    });

    return allItems.reduce((acc, curr) => {
      const group = getDateGroupLabel(curr.date);
      acc[group] = acc[group] || [];
      acc[group].push(curr);
      return acc;
    }, {} as GroupedHistory);
  };
  const uniqueStatuses = Array.from(
    new Set(statusChanges.map((sc) => sc.to?.trim()).filter(Boolean))
  );

  const pipelineStatuses = uniqueStatuses.map((status) => ({
    name: status,
    className: statusClassMap[status] || statusClassMap["default"],
    icon: statusIconMap[status] || <i className="ti ti-circle me-1" />,
  }));

  const latestStatus = pipelineStatuses[pipelineStatuses.length - 1]?.name;

  return (
    <>
        <PageLoadProgressBar />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      <i className="ti ti-medal text-primary me-2" />
                      Deals Overview </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              {/* Deals User */}
              <div className="contact-head">
                <div className="row align-items-center">
                  <div className="col-sm-8">
                    <ul className="contact-breadcrumb">
                      <li>
                        <Link to={route.deals}>
                          <i className="ti ti-arrow-narrow-left" />
                          Deals
                        </Link>
                      </li>
                      <li>
                        {deal?.contact?.contact_person_name
                          ? deal.contact.contact_person_name
                              .substring(0, 2)
                              .toUpperCase()
                          : "NA"}
                      </li>
                    </ul>
                  </div>
                  {/* <div className="col-sm-4 text-sm-end">
                    <div className="contact-pagination">
                      <p>1 of 40</p>
                      <ul>
                        <li>
                          <Link to={route.dealsDetails}>
                            <i className="ti ti-chevron-left" />
                          </Link>
                        </li>
                        <li>
                          <Link to={route.dealsDetails}>
                            <i className="ti ti-chevron-right" />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div> */}
                </div>
              </div>
              <Link
                to={route.dealsDetails}
                state={deal}
                key={deal.deal_id}
                className="card mb-3 text-decoration-none text-dark"
              >
                <div className="card-body pb-2">
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div className="d-flex align-items-center mb-2">
                      <div className="avatar avatar-xxl me-3 flex-shrink-0 border p-2">
                        <h6 className="text-default fw-medium">
                          {deal?.contact?.contact_person_name
                            ? deal.contact.contact_person_name
                                .substring(0, 2)
                                .toUpperCase()
                            : "NA"}
                        </h6>
                      </div>
                      <div>
                        <h5 className="mb-1">
                          {deal.contact?.company_name || "Unknown Company"}
                          <span className="star-icon">
                            <i className="fa-solid fa-star" />
                          </span>
                        </h5>
                        <p className="mb-1">
                          <i className="ti ti-building" />{" "}
                          {deal.contact?.company_mail || "-"}
                        </p>
                        <p className="mb-0">
                          <i className="ti ti-map-pin-pin" />{" "}
                          {deal.contact?.branch?.[0]?.branch_address ||
                            "No address"}
                        </p>
                      </div>
                    </div>
                    <div className="contacts-action">
                      <span className="badge badge-light">
                        <i className="ti ti-lock" /> Private
                      </span>
                      <div className="mb-2">
                        <span
                          className={`text-white py-1 px-2 d-inline-flex align-items-center rounded ${
                            deal.status === "won" ? "bg-success" : "bg-danger"
                          }`}
                        >
                          <i className="ti ti-thumb-up me-2" />
                         {deal.status
  ? deal.status.charAt(0).toUpperCase() + deal.status.slice(1)
  : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              {/* /Deals User */}
            </div>
            {/* Deals Sidebar */}
            <div className="col-xl-3 theiaStickySidebar">
              <div className="card">
                <div className="card-body p-3">
                  <h6 className="mb-3 fw-semibold">Deal Information</h6>
                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Date Created</span>
                      <span className="col-6">
                        {new Date(deal.created_at).toLocaleString()}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Value</span>
                      <span className="col-6">
                        {deal.currency} {deal.deal_value}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Due Date</span>
                      <span className="col-6">{deal.due_date}</span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Follow Up</span>
                      <span className="col-6">
                        {deal.follow_up_date || "N/A"}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Source</span>
                      <span className="col-6">
                        {deal.dealsource?.deal_source_nema || "N/A"}
                      </span>
                    </li>
                  </ul>
                  <hr />
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <h6 className="mb-3 fw-semibold">Owner</h6>
                  </div>
                  <div className="d-flex align-items-center mb-3">
                    <div className="avatar avatar-md me-2">
                      <ImageWithBasePath
                        src="assets/img/profiles/avatar-21.jpg"
                        alt="Image"
                      />
                    </div>
                    <p>{deal.leads?.users?.name || "N/A"}</p>
                  </div>
                  <hr />
                  <h6 className="mb-3 fw-semibold">Product</h6>
                  <a
                    href="#"
                    className="badge bg-light-300 text-default me-2 mb-2"
                  >
                    {deal.product?.product_name || "N/A"}
                  </a>
                  <hr />
                  <h6 className="mb-3 fw-semibold">Priority</h6>
                  <div className="priority-info">
                    <span className="d-inline-flex align-items-center">
                      <i
                        className={`ti ti-square-rounded-filled me-1 text-${
                          deal.deal_priority === "high" ? "danger" : "success"
                        } circle`}
                      />
                      {deal.deal_priority
  ? deal.deal_priority.charAt(0).toUpperCase() + deal.deal_priority.slice(1)
  : "N/A"}
                    </span>
                  </div>

                  <hr />
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <h6 className="mb-3 fw-semibold">Contacts</h6>
                  </div>
                  <div className="d-flex align-items-center mb-3">
                    <div className="avatar avatar-md me-2">
                      <ImageWithBasePath
                        src="assets/img/profiles/avatar-21.jpg"
                        alt="Contact"
                      />
                    </div>
                    <p>{deal.contact?.contact_person_name || "N/A"}</p>
                  </div>
                  <hr />
                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Last Modified</span>
                      <span className="col-6">
                        {new Date(deal.updated_at).toLocaleString()}
                      </span>
                    </li>
                    <li className="row mb-0">
                      <span className="col-6">Modified By</span>
                      <span className="col-6">
                        <span className="avatar avatar-xs me-1">
                          <ImageWithBasePath
                            src="assets/img/profiles/avatar-19.jpg"
                            className="avatar-xs"
                            alt="img"
                          />
                        </span>
                        Unknown
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* /Deals Sidebar */}
            {/* Deals Details */}
            <div className="col-xl-9">
              <div className="card mb-3">
                <div className="card-body pb-0">
                 <h4 className="fw-semibold mb-3">Deal Pipeline Status</h4>
<div className="pipeline-list">
  <ul className="d-flex list-unstyled gap-3 flex-wrap align-items-center">
    {pipelineStatuses.map((statusObj) => {
      const isActive = statusObj.name === latestStatus;

      return (
        <li key={statusObj.name}>
          <Link
            to="#"
            className={`${statusObj.className} px-4 py-2 rounded text-white fw-semibold shadow-sm d-inline-block`}
            style={{
              minWidth: "140px",
              textAlign: "center",
              userSelect: "none",
              transition: "background-color 0.3s ease",
              boxShadow: isActive
                ? "0 0 0 0.2rem rgba(0, 0, 0, 0.3)"
                : "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            {statusObj.icon}
            {statusObj.name}
          </Link>
        </li>
      );
    })}
  </ul>
</div>


                  <ul className="nav nav-tabs nav-tabs-bottom" role="tablist">
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#activities"
                        className="nav-link active"
                      >
                        <i className="ti ti-alarm-minus me-1" />
                        Activities
                      </Link>
                    </li>
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#status-timeline"
                        className="nav-link"
                      >
                        <i className="ti ti-notes me-1" />
                        Status Timeline
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              {/* Tab Content */}
              <div className="tab-content pt-0">
                {/* Activities */}
                <div className="tab-pane active show" id="activities">
                  <div className="card">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                      <h4 className="fw-semibold">Activities</h4>
                    </div>
                    <div className="card-body">
                      {Object.entries(timelineData).map(
                        ([group, items], idx) => (
                          <div key={idx} className="mb-3">
                            <Button
                              onClick={() => toggleDate(group)}
                              className="bg-white border-start border-4 rounded-2 shadow-sm mb-2 fw-semibold text-dark px-3 py-2 w-auto d-inline-block text-start"
                            >
                              {group}
                              <span className="float-end">
                                {openDates.includes(group) ? "−" : "+"}
                              </span>
                            </Button>

                            <div
                              className={`collapse ${
                                openDates.includes(group) ? "show" : ""
                              }`}
                              id={`collapse-${group}`}
                            >
                              {items.map((item, i) => {
                                const localDateTime = new Date(
                                  `${item.date}T${item.time}`
                                );
                                if (isNaN(localDateTime.getTime())) return null;

                                return (
                                  <div key={i} className="d-flex mt-3">
                                    <div
                                      className="text-nowrap me-3 text-dark fw-semibold"
                                      style={{ width: "70px" }}
                                    >
                                      {localDateTime.toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </div>
                                    <div className="me-3">
                                      <div
                                        className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                                        style={{ width: 28, height: 28 }}
                                      >
                                        {item.type === "created" ? (
                                          <FaPlus size={12} />
                                        ) : (
                                          <FaPen size={12} />
                                        )}
                                      </div>
                                    </div>
                                    <div className="bg-white p-3 rounded shadow-sm border w-100">
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: item.changes,
                                        }}
                                      />
                                      <div className="text-muted small mt-1">
                                        by {item.modified_by} on{" "}
                                        {localDateTime.toLocaleDateString(
                                          undefined,
                                          {
                                            weekday: "short",
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                          }
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
                <div className="tab-pane" id="status-timeline">
                  <div className="card">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                      <h4 className="fw-semibold">Status Timeline</h4>
                    </div>
                    <div className="card-body">
                      {statusChanges.map((change, index) => {
                        const changeDate = new Date(change.changedAt);
                        return (
                          <div key={index} className="d-flex mb-3">
                            <div
                              className="text-nowrap me-3 text-dark fw-semibold"
                              style={{ width: "70px", minWidth: "70px" }}
                            >
                              {changeDate.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                            <div className="me-3">
                              <div
                                className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: 28, height: 28 }}
                              >
                                <i
                                  className="ti ti-refresh"
                                  style={{ fontSize: 12 }}
                                ></i>
                              </div>
                            </div>
                            <div
                              className="bg-white p-3 rounded shadow-sm border w-100"
                              style={{ minWidth: 0 }}
                            >
                              <div>
                                <strong>
                                  {change.from} → {change.to}
                                </strong>
                              </div>
                              <div className="text-muted small mt-1">
                                by {change.changedBy} on{" "}
                                {changeDate.toLocaleDateString(undefined, {
                                  weekday: "short",
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* /Activities */}
              </div>
              {/* /Tab Content */}
            </div>
            {/* /Deals Details */}
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default DealsDetails;
